import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { 
  Users, 
  Shield, 
  TrendingUp, 
  DollarSign, 
  Search, 
  Filter,
  LogOut,
  UserCheck,
  UserX,
  Edit,
  Eye,
  Plus,
  FileText,
  BookOpen,
  Calendar,
  Award,
  Download,
  Upload,
  Trash2,
  Settings,
  Bell,
  MessageSquare,
  RefreshCw,
  Star,
  UserPlus,
  UserMinus,
  Key
} from 'lucide-react';

interface GeneralEnquiry {
  id: string;
  user_email: string;
  user_name: string;
  enquiry_type: string;
  subject: string;
  message: string;
  priority: string;
  status: string;
  admin_response: string;
  admin_notes: string;
  created_at: string;
  updated_at: string;
  resolved_at: string;
}

interface EnrollmentRequest {
  id: string;
  user_email: string;
  user_name: string;
  course_title: string;
  course_instructor: string;
  original_price: number;
  discounted_price: number;
  discount_percentage: number;
  membership_type: string;
  additional_notes: string;
  status: string;
  created_at: string;
}

interface MemberProfile {
  id: string;
  full_name: string;
  email: string;
  organization: string;
  membership_status: string;
  payment_status: string;
  membership_start_date: string;
  membership_end_date: string;
  created_at: string;
  membership_types_2025_12_03_10_30: {
    name: string;
    price_eur: number;
  };
}

interface Resource {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
  category: string;
  membership_types: string[];
  is_active: boolean;
  created_at: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration_hours: number;
  price_eur: number;
  discount_percentage: number;
  course_url: string;
  category: string;
  difficulty_level: string;
  membership_types: string[];
  is_active: boolean;
}

interface Event {
  id: string;
  title: string;
  description: string;
  event_date: string;
  location: string;
  event_type: string;
  registration_url: string;
  membership_types: string[];
  is_active: boolean;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  badge_image_url: string;
  criteria: string;
  points: number;
  category: string;
  is_active: boolean;
}

interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  pendingApplications: number;
  totalRevenue: number;
  totalResources: number;
  totalCourses: number;
  totalEvents: number;
  totalAchievements: number;
}

const EnhancedAdminDashboard = () => {
  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [membershipTypes, setMembershipTypes] = useState<any[]>([]);
  const [enrollmentRequests, setEnrollmentRequests] = useState<EnrollmentRequest[]>([]);
  const [generalEnquiries, setGeneralEnquiries] = useState<GeneralEnquiry[]>([]);
  
  // Manual achievement management state
  const [awardEmail, setAwardEmail] = useState('');
  const [removeEmail, setRemoveEmail] = useState('');
  const [selectedAchievementToAward, setSelectedAchievementToAward] = useState('');
  const [selectedAchievementToRemove, setSelectedAchievementToRemove] = useState('');
  const [awardingAchievement, setAwardingAchievement] = useState(false);
  const [removingAchievement, setRemovingAchievement] = useState(false);
  const [refreshingAchievements, setRefreshingAchievements] = useState(false);
  
  // Manual member management state
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberPassword, setNewMemberPassword] = useState('');
  const [selectedMembershipType, setSelectedMembershipType] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [deletingMember, setDeletingMember] = useState('');
  const [stats, setStats] = useState<DashboardStats>({
    totalMembers: 0,
    activeMembers: 0,
    pendingApplications: 0,
    totalRevenue: 0,
    totalResources: 0,
    totalCourses: 0,
    totalEvents: 0,
    totalAchievements: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('analytics');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Form states for adding/editing content
  const [newResource, setNewResource] = useState({
    title: '', description: '', file_url: '', file_type: '', category: '', membership_types: []
  });
  const [newCourse, setNewCourse] = useState({
    title: '', description: '', instructor: '', duration_hours: 0, price_eur: 0, course_url: '', category: '', difficulty_level: '', membership_types: []
  });
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_date: '', location: '', event_type: '', registration_url: '', membership_types: []
  });
  const [newAchievement, setNewAchievement] = useState({
    title: '', description: '', badge_image_url: '', criteria: '', points: 0, category: ''
  });

  // Edit states
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState<'resource' | 'course' | 'event' | 'achievement' | null>(null);

  // Changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({
    resources: false,
    courses: false,
    events: false,
    achievements: false
  });
  const [saveLoading, setSaveLoading] = useState(false);
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchMembers(),
      fetchResources(),
      fetchCourses(),
      fetchEvents(),
      fetchAchievements(),
      fetchMembershipTypes(),
      fetchEnrollmentRequests(),
      fetchGeneralEnquiries(),
      fetchStats()
    ]);
    setLoading(false);
  };

  const fetchMembers = async () => {
    console.log('Fetching members from database...');
    const { data, error } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .select(`
        *,
        membership_types_2025_12_03_10_30 (
          id,
          name,
          price_eur
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching members:', error);
    } else {
      console.log('Fetched members count:', data?.length);
      console.log('All member emails:', data?.map(m => m.email));
      console.log('Looking for mirelakb23@gmail.com:', data?.find(m => m.email === 'mirelakb23@gmail.com'));
      setMembers(data || []);
    }
  };

  const handleRefreshMembers = async () => {
    console.log('Manual member refresh triggered');
    await fetchMembers();
    toast({
      title: "Members Refreshed",
      description: "Member list has been refreshed from database.",
    });
  };

  const createMissingProfiles = async () => {
    try {
      console.log('Creating missing member profiles...');
      const { data, error } = await supabase.rpc('create_missing_member_profiles');
      
      if (error) {
        console.error('Error creating missing profiles:', error);
        toast({
          title: "Profile Creation Error",
          description: `Error: ${error.message}`,
          variant: "destructive",
        });
      } else {
        console.log('Profile creation result:', data);
        toast({
          title: "Profiles Created",
          description: data || "Missing member profiles have been created.",
        });
        
        // Refresh member list
        await fetchMembers();
      }
    } catch (error) {
      console.error('Exception creating profiles:', error);
      toast({
        title: "Profile Creation Failed",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const addAuthCredentials = async (memberEmail: string, memberName: string) => {
    const password = prompt(`Enter password for ${memberName} (${memberEmail}):`);
    
    if (!password) {
      console.log('Password entry cancelled');
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log('Adding auth credentials for:', memberEmail);
      
      const { data, error } = await supabase.functions.invoke('add_auth_credentials_robust_2025_12_04_14_00', {
        body: {
          memberEmail: memberEmail,
          password: password
        }
      });
      
      console.log('Add auth credentials response:', { data, error });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Edge function returned failure:', data);
        throw new Error(data?.error || 'Unknown error from edge function');
      }
      
      console.log('Auth credentials added successfully:', data.message);
      
      // Refresh member list
      await fetchMembers();
      
      toast({
        title: "Login Credentials Added",
        description: data.message,
      });
      
    } catch (error) {
      console.error('Error adding auth credentials:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Failed to Add Credentials",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const updateMemberDates = async (memberId: string, memberName: string, currentStartDate: string, currentEndDate?: string) => {
    const startDate = prompt(`Enter start date for ${memberName} (YYYY-MM-DD):`, currentStartDate || '');
    
    if (startDate === null) {
      console.log('Date update cancelled');
      return;
    }
    
    // Validate date format
    if (startDate && !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      toast({
        title: "Invalid Date Format",
        description: "Please use YYYY-MM-DD format (e.g., 2024-01-15).",
        variant: "destructive",
      });
      return;
    }
    
    const endDate = prompt(`Enter end date for ${memberName} (YYYY-MM-DD, or leave empty for no end date):`, currentEndDate || '');
    
    if (endDate === null) {
      console.log('Date update cancelled');
      return;
    }
    
    // Validate end date format if provided
    if (endDate && !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      toast({
        title: "Invalid Date Format",
        description: "Please use YYYY-MM-DD format (e.g., 2024-12-31).",
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log('Updating member dates:', { memberId, startDate, endDate });
      
      const { data, error } = await supabase.rpc('update_member_dates', {
        p_member_id: memberId,
        p_start_date: startDate,
        p_end_date: endDate || null
      });
      
      console.log('Update member dates response:', { data, error });
      
      if (error) {
        console.error('Database function error:', error);
        throw new Error(`Date update failed: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Function returned failure:', data);
        throw new Error(data?.error || 'Unknown error occurred');
      }
      
      console.log('Member dates updated successfully:', data.message);
      
      // Refresh member list
      await fetchMembers();
      
      toast({
        title: "Dates Updated",
        description: `Successfully updated membership dates for ${memberName}.`,
      });
      
    } catch (error) {
      console.error('Error updating member dates:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Date Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const fetchResources = async () => {
    const { data, error } = await supabase
      .from('resources_2025_12_03_10_30')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resources:', error);
    } else {
      setResources(data || []);
    }
  };

  const fetchCourses = async () => {
    const { data, error } = await supabase
      .from('courses_2025_12_03_10_30')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching courses:', error);
    } else {
      setCourses(data || []);
    }
  };

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('events_2025_12_03_10_30')
      .select('*')
      .order('event_date', { ascending: true });

    if (error) {
      console.error('Error fetching events:', error);
    } else {
      setEvents(data || []);
    }
  };

  const fetchAchievements = async () => {
    const { data, error } = await supabase
      .from('achievements_2025_12_03_10_30')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching achievements:', error);
    } else {
      setAchievements(data || []);
    }
  };

  const fetchStats = async () => {
    const { data: allMembers } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .select(`
        membership_status,
        payment_status,
        membership_types_2025_12_03_10_30 (
          price_eur
        )
      `);

    const { data: resourcesCount } = await supabase
      .from('resources_2025_12_03_10_30')
      .select('id', { count: 'exact' });

    const { data: coursesCount } = await supabase
      .from('courses_2025_12_03_10_30')
      .select('id', { count: 'exact' });

    const { data: eventsCount } = await supabase
      .from('events_2025_12_03_10_30')
      .select('id', { count: 'exact' });

    const { data: achievementsCount } = await supabase
      .from('achievements_2025_12_03_10_30')
      .select('id', { count: 'exact' });

    if (allMembers) {
      const totalMembers = allMembers.length;
      const activeMembers = allMembers.filter(m => m.membership_status === 'active').length;
      const pendingApplications = allMembers.filter(m => m.membership_status === 'pending').length;
      const totalRevenue = allMembers
        .filter(m => m.payment_status === 'paid')
        .reduce((sum, m) => sum + (m.membership_types_2025_12_03_10_30?.price_eur || 0), 0);

      setStats({
        totalMembers,
        activeMembers,
        pendingApplications,
        totalRevenue,
        totalResources: resourcesCount?.length || 0,
        totalCourses: coursesCount?.length || 0,
        totalEvents: eventsCount?.length || 0,
        totalAchievements: achievementsCount?.length || 0
      });
    }
  };

  const fetchMembershipTypes = async () => {
    const { data, error } = await supabase
      .from('membership_types_2025_12_03_10_30')
      .select('*')
      .order('price_eur', { ascending: true });

    if (error) {
      console.error('Error fetching membership types:', error);
    } else {
      console.log('Fetched membership types:', data);
      setMembershipTypes(data || []);
    }
  };

  const fetchEnrollmentRequests = async () => {
    console.log('Admin fetching enrollment requests...');
    console.log('Current user:', await supabase.auth.getUser());
    
    try {
      // First try regular query
      const { data, error } = await supabase
        .from('course_enrollment_requests_2025_12_03_12_00')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Enrollment requests query result:', { data, error, count: data?.length });

      if (error) {
        console.error('Error fetching enrollment requests:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // If regular query fails, try with RPC or direct approach
        console.log('Trying alternative approach...');
        
        // Create a simple RPC function call or use service role approach
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_all_enrollment_requests');
        
        if (rpcError) {
          console.error('RPC also failed:', rpcError);
          // Set empty array but don't throw error
          setEnrollmentRequests([]);
        } else {
          console.log('RPC succeeded:', rpcData?.length || 0);
          setEnrollmentRequests(rpcData || []);
        }
      } else {
        console.log('Successfully fetched enrollment requests:', data?.length || 0);
        setEnrollmentRequests(data || []);
      }
    } catch (err) {
      console.error('Unexpected error fetching enrollment requests:', err);
      setEnrollmentRequests([]);
    }
  };

  const fetchGeneralEnquiries = async () => {
    console.log('Admin fetching general enquiries...');
    
    try {
      // First try regular query
      const { data, error } = await supabase
        .from('general_enquiries_2025_12_03_13_00')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('General enquiries query result:', { data, error, count: data?.length });

      if (error) {
        console.error('Error fetching general enquiries:', error);
        
        // Try RPC fallback
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_all_general_enquiries');
        
        if (rpcError) {
          console.error('RPC also failed for enquiries:', rpcError);
          setGeneralEnquiries([]);
        } else {
          console.log('RPC succeeded for enquiries:', rpcData?.length || 0);
          setGeneralEnquiries(rpcData || []);
        }
      } else {
        console.log('Successfully fetched general enquiries:', data?.length || 0);
        setGeneralEnquiries(data || []);
      }
    } catch (err) {
      console.error('Unexpected error fetching general enquiries:', err);
      setGeneralEnquiries([]);
    }
  };

  // Manual achievement management functions
  const handleAwardAchievement = async () => {
    if (!awardEmail || !selectedAchievementToAward) {
      toast({
        title: "Missing Information",
        description: "Please provide both email and achievement.",
        variant: "destructive",
      });
      return;
    }

    setAwardingAchievement(true);
    try {
      const { data, error } = await supabase.rpc('admin_award_achievement', {
        target_user_email: awardEmail,
        achievement_title: selectedAchievementToAward
      });

      if (error) {
        throw new Error(error.message);
      }

      const result = data as { success: boolean; message?: string; error?: string };
      
      if (result.success) {
        toast({
          title: "Achievement Awarded!",
          description: `Successfully awarded "${selectedAchievementToAward}" to ${awardEmail}.`,
        });
        setAwardEmail('');
        setSelectedAchievementToAward('');
        // Refresh data to show updated achievements
        fetchAllData();
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error awarding achievement:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Award Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAwardingAchievement(false);
    }
  };

  const handleRemoveAchievement = async () => {
    if (!removeEmail || !selectedAchievementToRemove) {
      toast({
        title: "Missing Information",
        description: "Please provide both email and achievement.",
        variant: "destructive",
      });
      return;
    }

    setRemovingAchievement(true);
    try {
      const { data, error } = await supabase.rpc('admin_remove_achievement', {
        target_user_email: removeEmail,
        achievement_title: selectedAchievementToRemove
      });

      if (error) {
        throw new Error(error.message);
      }

      const result = data as { success: boolean; message?: string; error?: string };
      
      if (result.success) {
        toast({
          title: "Achievement Removed!",
          description: 'Successfully removed ' + selectedAchievementToRemove + ' from ' + removeEmail + '.'
        });
        setRemoveEmail('');
        setSelectedAchievementToRemove('');
        // Refresh data to show updated achievements
        fetchAllData();
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error removing achievement:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Removal Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setRemovingAchievement(false);
    }
  };

  const handleRefreshAllAchievements = async () => {
    setRefreshingAchievements(true);
    try {
      const { data, error } = await supabase.rpc('refresh_all_achievements');

      if (error) {
        throw new Error(error.message);
      }

      const result = data as { success: boolean; total_achievements_awarded?: number; error?: string };
      
      if (result.success) {
        toast({
          title: "Achievements Refreshed!",
          description: `Successfully awarded ${result.total_achievements_awarded || 0} achievements to all members.`,
        });
        // Refresh all data to show updated achievements
        fetchAllData();
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error refreshing all achievements:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Refresh Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setRefreshingAchievements(false);
    }
  };

  const handleStatusUpdate = async (memberId: string, newStatus: string) => {
    const { error } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .update({ membership_status: newStatus })
      .eq('id', memberId);

    if (!error) {
      fetchMembers();
      fetchStats();
    }
  };

  const handlePaymentStatusUpdate = async (memberId: string, newStatus: string) => {
    const { error } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .update({ payment_status: newStatus })
      .eq('id', memberId);

    if (!error) {
      fetchMembers();
      fetchStats();
    }
  };

  const handleMembershipTierUpdate = async (memberId: string, newTierId: string) => {
    console.log('Updating membership tier:', { memberId, newTierId });
    
    const { data, error } = await supabase
      .from('member_profiles_2025_12_03_10_30')
      .update({ membership_type_id: newTierId })
      .eq('id', memberId)
      .select();

    if (error) {
      console.error('Error updating membership tier:', error);
    } else {
      console.log('Membership tier updated successfully:', data);
      fetchMembers();
      fetchStats();
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail || !newMemberName || !newMemberPassword || !selectedMembershipType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setAddingMember(true);
    try {
      // Use edge function to create member with proper admin privileges
      const { data, error } = await supabase.functions.invoke('admin_create_member_with_auth_id_2025_12_04_14_00', {
        body: {
          email: newMemberEmail,
          full_name: newMemberName,
          password: newMemberPassword,
          membership_type_id: selectedMembershipType
        }
      });

      console.log('Edge function response:', { data, error });
      
      if (error) {
        console.error('Edge function invocation error:', error);
        throw new Error(`Edge function invocation failed: ${error.message}`);
      }

      if (!data) {
        throw new Error('No response data from edge function');
      }

      if (!data.success) {
        console.error('Edge function returned error:', data);
        throw new Error(`Member creation failed at step '${data.step || 'unknown'}': ${data.error || 'Unknown error'}`);
      }

      // Clear form
      setNewMemberEmail('');
      setNewMemberName('');
      setNewMemberPassword('');
      setSelectedMembershipType('');

      // Refresh data
      fetchMembers();
      fetchStats();

      toast({
        title: "Member Added",
        description: `Successfully added ${newMemberName} as a new member.`,
      });
    } catch (error) {
      console.error('Error adding member:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Add Member Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAddingMember(false);
    }
  };

  const testEdgeFunction = async () => {
    try {
      console.log('Testing edge function...');
      const { data, error } = await supabase.functions.invoke('test_admin_auth_2025_12_04_14_00');
      
      if (error) {
        console.error('Edge function test error:', error);
        toast({
          title: "Test Failed",
          description: `Edge function error: ${error.message}`,
          variant: "destructive",
        });
        return;
      }
      
      console.log('Edge function test result:', data);
      toast({
        title: "Test Successful",
        description: data.success ? data.message : 'Test completed',
      });
    } catch (error) {
      console.error('Test error:', error);
      toast({
        title: "Test Error",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: "destructive",
      });
    }
  };

  const handleAddMemberDirectly = async () => {
    if (!newMemberEmail || !newMemberName || !selectedMembershipType) {
      toast({
        title: "Missing Information",
        description: "Please fill in email, name, and membership type.",
        variant: "destructive",
      });
      return;
    }

    setAddingMember(true);
    try {
      console.log('Creating member directly in database...');
      
      // Check if member already exists
      const { data: existingMember } = await supabase
        .from('member_profiles_2025_12_03_10_30')
        .select('email')
        .eq('email', newMemberEmail)
        .single();
      
      if (existingMember) {
        throw new Error('A member with this email already exists');
      }

      // Use SQL function to create member profile directly
      console.log('Attempting direct profile creation via SQL function with data:', {
        email: newMemberEmail,
        full_name: newMemberName,
        membership_type_id: selectedMembershipType
      });
      
      const { data: functionResult, error: functionError } = await supabase
        .rpc('simple_add_member_bypass', {
          p_email: newMemberEmail,
          p_full_name: newMemberName,
          p_membership_type_id: selectedMembershipType
        });

      if (functionError) {
        console.error('SQL function error:', functionError);
        throw new Error(`SQL function failed: ${functionError.message}`);
      }

      console.log('SQL function result:', functionResult);
      
      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Unknown error from SQL function');
      }

      // Clear form
      setNewMemberEmail('');
      setNewMemberName('');
      setNewMemberPassword('');
      setSelectedMembershipType('');

      // Refresh data
      fetchMembers();
      fetchStats();

      toast({
        title: "Member Added (Profile Only)",
        description: functionResult.message || `Successfully added ${newMemberName}. Note: No login credentials created - profile only.`,
      });
    } catch (error) {
      console.error('Error adding member directly:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Direct Add Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleAddMemberMinimal = async () => {
    if (!newMemberEmail || !newMemberName) {
      toast({
        title: "Missing Information",
        description: "Please fill in email and name (minimal test).",
        variant: "destructive",
      });
      return;
    }

    setAddingMember(true);
    try {
      console.log('Testing minimal member creation...');
      
      // Try the most basic insert possible with auth_id
      const { data: profileData, error: profileError } = await supabase
        .from('member_profiles_2025_12_03_10_30')
        .insert({
          email: newMemberEmail,
          full_name: newMemberName,
          auth_id: null  // Set auth_id to null for profile-only creation
        })
        .select();

      if (profileError) {
        console.error('Minimal insert error:', profileError);
        throw new Error(`Minimal insert failed: ${profileError.message}`);
      }

      console.log('Minimal insert success:', profileData);

      // Clear form
      setNewMemberEmail('');
      setNewMemberName('');
      setNewMemberPassword('');
      setSelectedMembershipType('');

      // Refresh data
      fetchMembers();
      fetchStats();

      toast({
        title: "Minimal Member Added",
        description: `Successfully added ${newMemberName} with minimal data.`,
      });
    } catch (error) {
      console.error('Error adding member minimally:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Minimal Add Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleDeleteMember = async (memberEmail: string, memberName: string) => {
    console.log('Delete member clicked:', { memberEmail, memberName });
    
    // Use window.confirm to ensure it works
    const confirmed = window.confirm(`Are you sure you want to delete member ${memberName} (${memberEmail})? This action cannot be undone.`);
    console.log('Confirmation result:', confirmed);
    
    if (!confirmed) {
      console.log('Deletion cancelled by user');
      return;
    }

    console.log('Starting member deletion process...');
    setDeletingMember(memberEmail);
    try {
      console.log('Calling edge function for member deletion...');
      // Use edge function to delete member with proper admin privileges
      const { data, error } = await supabase.functions.invoke('admin_delete_member_fixed_2025_12_04_14_00', {
        body: {
          memberEmail: memberEmail,
          memberName: memberName
        }
      });

      console.log('Edge function response:', { data, error });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Edge function returned failure:', data);
        throw new Error(data?.error || 'Unknown error from edge function');
      }
      
      console.log('Member deletion successful:', data.message);

      // Refresh data
      fetchMembers();
      fetchStats();

      toast({
        title: "Member Deleted",
        description: `Successfully deleted ${memberName} and all associated data.`,
      });
    } catch (error) {
      console.error('Error deleting member:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Delete Member Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setDeletingMember('');
    }
  };

  const handleAddResource = async () => {
    const { error } = await supabase
      .from('resources_2025_12_03_10_30')
      .insert([newResource]);

    if (!error) {
      fetchResources();
      fetchStats();
      setNewResource({ title: '', description: '', file_url: '', file_type: '', category: '', membership_types: [] });
    }
  };

  const handleAddCourse = async () => {
    const { error } = await supabase
      .from('courses_2025_12_03_10_30')
      .insert([newCourse]);

    if (!error) {
      fetchCourses();
      fetchStats();
      setNewCourse({ title: '', description: '', instructor: '', duration_hours: 0, price_eur: 0, course_url: '', category: '', difficulty_level: '', membership_types: [] });
    }
  };

  const handleAddEvent = async () => {
    const { error } = await supabase
      .from('events_2025_12_03_10_30')
      .insert([newEvent]);

    if (!error) {
      fetchEvents();
      fetchStats();
      setNewEvent({ title: '', description: '', event_date: '', location: '', event_type: '', registration_url: '', membership_types: [] });
    }
  };

  const handleAddAchievement = async () => {
    const { error } = await supabase
      .from('achievements_2025_12_03_10_30')
      .insert([newAchievement]);

    if (!error) {
      fetchAchievements();
      fetchStats();
      setNewAchievement({ title: '', description: '', badge_image_url: '', criteria: '', points: 0, category: '' });
    }
  };

  const handleDeleteResource = async (id: string) => {
    const { error } = await supabase
      .from('resources_2025_12_03_10_30')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchResources();
      fetchStats();
    }
  };

  const handleDeleteCourse = async (id: string) => {
    const { error } = await supabase
      .from('courses_2025_12_03_10_30')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchCourses();
      fetchStats();
    }
  };

  const handleDeleteEvent = async (id: string) => {
    const { error } = await supabase
      .from('events_2025_12_03_10_30')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchEvents();
      fetchStats();
    }
  };

  const handleDeleteAchievement = async (id: string) => {
    const { error } = await supabase
      .from('achievements_2025_12_03_10_30')
      .delete()
      .eq('id', id);

    if (!error) {
      fetchAchievements();
      fetchStats();
    }
  };

  // Edit functions
  const handleEditResource = (resource: Resource) => {
    setEditingResource(resource);
    setEditType('resource');
    setIsEditDialogOpen(true);
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setEditType('course');
    setIsEditDialogOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setEditType('event');
    setIsEditDialogOpen(true);
  };

  const handleEditAchievement = (achievement: Achievement) => {
    setEditingAchievement(achievement);
    setEditType('achievement');
    setIsEditDialogOpen(true);
  };

  const handleUpdateResource = async () => {
    if (!editingResource) return;

    const { error } = await supabase
      .from('resources_2025_12_03_10_30')
      .update(editingResource)
      .eq('id', editingResource.id);

    if (!error) {
      fetchResources();
      fetchStats();
      setIsEditDialogOpen(false);
      setEditingResource(null);
      setHasUnsavedChanges(prev => ({ ...prev, resources: true }));
    }
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse) return;

    const { error } = await supabase
      .from('courses_2025_12_03_10_30')
      .update(editingCourse)
      .eq('id', editingCourse.id);

    if (!error) {
      fetchCourses();
      fetchStats();
      setIsEditDialogOpen(false);
      setEditingCourse(null);
      setHasUnsavedChanges(prev => ({ ...prev, courses: true }));
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    const { error } = await supabase
      .from('events_2025_12_03_10_30')
      .update(editingEvent)
      .eq('id', editingEvent.id);

    if (!error) {
      fetchEvents();
      fetchStats();
      setIsEditDialogOpen(false);
      setEditingEvent(null);
      setHasUnsavedChanges(prev => ({ ...prev, events: true }));
    }
  };

  const handleUpdateAchievement = async () => {
    if (!editingAchievement) return;

    const { error } = await supabase
      .from('achievements_2025_12_03_10_30')
      .update(editingAchievement)
      .eq('id', editingAchievement.id);

    if (!error) {
      fetchAchievements();
      fetchStats();
      setIsEditDialogOpen(false);
      setEditingAchievement(null);
      setHasUnsavedChanges(prev => ({ ...prev, achievements: true }));
    }
  };

  // Save changes functions
  const handleSaveChanges = async (type: 'resources' | 'courses' | 'events' | 'achievements') => {
    setSaveLoading(true);
    try {
      // Refresh data to ensure changes are visible
      switch (type) {
        case 'resources':
          await fetchResources();
          break;
        case 'courses':
          await fetchCourses();
          break;
        case 'events':
          await fetchEvents();
          break;
        case 'achievements':
          await fetchAchievements();
          break;
      }
      await fetchStats();
      setHasUnsavedChanges(prev => ({ ...prev, [type]: false }));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.organization?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || member.membership_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-lg font-medium">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">CSI Admin Dashboard</h1>
            <p className="text-muted-foreground">Comprehensive membership and content management</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="achievements">Achievements</TabsTrigger>
            <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="enquiries">
              Enquiries
              {generalEnquiries.filter(eq => eq.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {generalEnquiries.filter(eq => eq.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalMembers}</div>
                  <p className="text-xs text-muted-foreground">Registered users</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Members</CardTitle>
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.activeMembers}</div>
                  <p className="text-xs text-muted-foreground">Active memberships</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Applications</CardTitle>
                  <UserX className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.pendingApplications}</div>
                  <p className="text-xs text-muted-foreground">Awaiting approval</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">€{stats.totalRevenue.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">From memberships</p>
                </CardContent>
              </Card>
            </div>

            {/* Content Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Resources</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalResources}</div>
                  <p className="text-xs text-muted-foreground">Available downloads</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Courses</CardTitle>
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalCourses}</div>
                  <p className="text-xs text-muted-foreground">Training courses</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Events</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalEvents}</div>
                  <p className="text-xs text-muted-foreground">Scheduled events</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Achievements</CardTitle>
                  <Award className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalAchievements}</div>
                  <p className="text-xs text-muted-foreground">Available badges</p>
                </CardContent>
              </Card>
            </div>

            {/* Notifications Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Enrollment Requests */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Course Enrollment Requests
                      {enrollmentRequests.filter(req => req.status === 'pending').length > 0 && (
                        <Badge variant="destructive" className="ml-2">
                          {enrollmentRequests.filter(req => req.status === 'pending').length}
                        </Badge>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        console.log('Manual refresh triggered');
                        fetchEnrollmentRequests();
                      }}
                    >
                      Refresh
                    </Button>
                  </CardTitle>
                  <CardDescription>Recent course enrollment requests from members</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {enrollmentRequests.filter(req => req.status === 'pending').slice(0, 5).map((request) => (
                      <div key={request.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-sm">{request.course_title}</h4>
                            <p className="text-sm text-muted-foreground">{request.user_name} ({request.user_email})</p>
                            <p className="text-xs text-muted-foreground">{request.membership_type} Member</p>
                          </div>
                          <Badge variant="secondary">Pending</Badge>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <div>
                            <span className="line-through text-muted-foreground">€{request.original_price?.toFixed(2)}</span>
                            <span className="ml-2 font-semibold text-green-600">€{request.discounted_price?.toFixed(2)}</span>
                            <span className="ml-1 text-xs text-green-600">({request.discount_percentage}% off)</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(request.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {request.additional_notes && (
                          <div className="text-xs text-muted-foreground bg-gray-50 p-2 rounded">
                            <strong>Notes:</strong> {request.additional_notes}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={async () => {
                              const { error } = await supabase
                                .from('course_enrollment_requests_2025_12_03_12_00')
                                .update({ status: 'approved' })
                                .eq('id', request.id);
                              if (!error) {
                                fetchEnrollmentRequests();
                                toast({
                                  title: "Request Approved",
                                  description: 'Enrollment request for ' + request.course_title + ' has been approved.',
                                });
                              }
                            }}
                          >
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={async () => {
                              const { error } = await supabase
                                .from('course_enrollment_requests_2025_12_03_12_00')
                                .update({ status: 'rejected' })
                                .eq('id', request.id);
                              if (!error) {
                                fetchEnrollmentRequests();
                                toast({
                                  title: "Request Rejected",
                                  description: 'Enrollment request for ' + request.course_title + ' has been rejected.',
                                });
                              }
                            }}
                          >
                            Reject
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              const subject = encodeURIComponent('Course Enrollment: ' + request.course_title);
                              const body = encodeURIComponent(
                                'Dear ' + request.user_name + ',\n\n' +
                                'Thank you for your interest in ' + request.course_title + '.\n\n' +
                                'Course Details:\n' +
                                '- Title: ' + request.course_title + '\n' +
                                '- Instructor: ' + request.course_instructor + '\n' +
                                '- Your Price: €' + request.discounted_price?.toFixed(2) + ' (' + request.discount_percentage + '% member discount)\n\n' +
                                'Please reply to this email to proceed with your enrollment.\n\n' +
                                'Best regards,\nCSI Team'
                              );
                              window.open('mailto:' + request.user_email + '?subject=' + subject + '&body=' + body);
                            }}
                          >
                            Contact
                          </Button>
                        </div>
                      </div>
                    ))}
                    {enrollmentRequests.filter(req => req.status === 'pending').length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No pending enrollment requests</p>
                      </div>
                    )}
                    {enrollmentRequests.filter(req => req.status === 'pending').length > 5 && (
                      <div className="text-center pt-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setActiveTab('enrollments')}
                        >
                          View All ({enrollmentRequests.filter(req => req.status === 'pending').length}) Requests
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>Latest system activities and updates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Recent Enrollments */}
                    {enrollmentRequests.filter(req => req.status !== 'pending').slice(0, 3).map((request) => (
                      <div key={request.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className={'h-2 w-2 rounded-full ' + (
                          request.status === 'approved' ? 'bg-green-500' : 
                          request.status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'
                        )} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {request.status === 'approved' ? 'Approved' : 
                             request.status === 'rejected' ? 'Rejected' : 'Processed'} enrollment
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {request.user_name} - {request.course_title}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                    
                    {/* Recent Member Registrations */}
                    {members.slice(0, 2).map((member) => (
                      <div key={member.id} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">New member registered</p>
                          <p className="text-xs text-muted-foreground">
                            {member.full_name} - {member.membership_types_2025_12_03_10_30?.name}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(member.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                    
                    {enrollmentRequests.length === 0 && members.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No recent activity</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* General Enquiries Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    General Enquiries
                    {generalEnquiries.filter(eq => eq.status === 'pending').length > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {generalEnquiries.filter(eq => eq.status === 'pending').length}
                      </Badge>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      console.log('Manual refresh enquiries triggered');
                      fetchGeneralEnquiries();
                    }}
                  >
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription>Member enquiries about resources, events, achievements, and more</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {generalEnquiries.filter(eq => eq.status === 'pending').slice(0, 5).map((enquiry) => (
                    <div key={enquiry.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-sm">{enquiry.subject}</h4>
                          <p className="text-sm text-muted-foreground">{enquiry.user_name} ({enquiry.user_email})</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs capitalize">
                              {enquiry.enquiry_type}
                            </Badge>
                            <Badge 
                              variant="secondary"
                              className={'text-xs ' + (
                                enquiry.priority === 'urgent' ? 'bg-red-100 text-red-800 border-red-300' :
                                enquiry.priority === 'high' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                enquiry.priority === 'normal' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                'bg-gray-100 text-gray-800 border-gray-300'
                              )}
                            >
                              {enquiry.priority} priority
                            </Badge>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded">
                          {enquiry.message.length > 150 ? enquiry.message.substring(0, 150) + '...' : enquiry.message}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={async () => {
                            const { error } = await supabase
                              .from('general_enquiries_2025_12_03_13_00')
                              .update({ status: 'in_progress' })
                              .eq('id', enquiry.id);
                            if (!error) {
                              fetchGeneralEnquiries();
                              toast({
                                title: "Status Updated",
                                  description: 'Enquiry marked as in progress.',
                              });
                            }
                          }}
                        >
                          Mark In Progress
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            const { error } = await supabase
                              .from('general_enquiries_2025_12_03_13_00')
                              .update({ status: 'resolved', resolved_at: new Date().toISOString() })
                              .eq('id', enquiry.id);
                            if (!error) {
                              fetchGeneralEnquiries();
                              toast({
                                title: "Enquiry Resolved",
                                  description: 'Enquiry has been marked as resolved.',
                              });
                            }
                          }}
                        >
                          Mark Resolved
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            const subject = encodeURIComponent('Re: ' + enquiry.subject);
                            const body = encodeURIComponent(
                              'Dear ' + enquiry.user_name + ',\n\n' +
                              'Thank you for your enquiry about ' + enquiry.enquiry_type + '.\n\n' +
                              'Your message: "' + enquiry.message + '"\n\n' +
                              '[Please provide your response here]\n\n' +
                              'Best regards,\nCSI Admin Team'
                            );
                            window.open('mailto:' + enquiry.user_email + '?subject=' + subject + '&body=' + body);
                          }}
                        >
                          Reply
                        </Button>
                      </div>
                    </div>
                  ))}
                  {generalEnquiries.filter(eq => eq.status === 'pending').length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No pending enquiries</p>
                    </div>
                  )}
                  {generalEnquiries.filter(eq => eq.status === 'pending').length > 5 && (
                    <div className="text-center pt-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setActiveTab('enquiries')}
                      >
                        View All ({generalEnquiries.filter(eq => eq.status === 'pending').length}) Enquiries
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Member Management</CardTitle>
                    <CardDescription>Manage member accounts, status, and payments</CardDescription>
                  </div>
                  <Button 
                    onClick={handleRefreshMembers}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh Members
                  </Button>
                  <Button 
                    onClick={() => searchSpecificMember('mirelakb23@gmail.com')}
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 ml-2"
                  >
                    <Search className="h-4 w-4" />
                    Create Missing Profiles
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Membership</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{member.full_name}</div>
                              <div className="text-sm text-muted-foreground">{member.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>{member.organization || 'N/A'}</TableCell>
                          <TableCell>
                            <div className="space-y-2">
                              <div className="font-medium text-sm">
                                {member.membership_types_2025_12_03_10_30?.name || 'N/A'}
                              </div>
                              <Select 
                                value={member.membership_type_id || member.membership_types_2025_12_03_10_30?.id || ''} 
                                onValueChange={(value) => {
                                  console.log('Membership change requested:', { 
                                    memberId: member.id, 
                                    currentTypeId: member.membership_type_id,
                                    currentTypeName: member.membership_types_2025_12_03_10_30?.name,
                                    newTypeId: value 
                                  });
                                  handleMembershipTierUpdate(member.id, value);
                                }}
                              >
                                <SelectTrigger className="w-[160px] h-8">
                                  <SelectValue placeholder="Change Tier" />
                                </SelectTrigger>
                                <SelectContent>
                                  {membershipTypes.map((type) => (
                                    <SelectItem key={type.id} value={type.id}>
                                      <div className="flex flex-col">
                                        <span className="font-medium">{type.name}</span>
                                        <span className="text-xs text-muted-foreground">€{type.price_eur}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              member.membership_status === 'active' ? 'default' :
                              member.membership_status === 'pending' ? 'secondary' :
                              member.membership_status === 'expired' ? 'destructive' : 'outline'
                            }>
                              {member.membership_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              member.payment_status === 'paid' ? 'default' :
                              member.payment_status === 'pending' ? 'secondary' : 'destructive'
                            }>
                              {member.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Select onValueChange={(value) => handleStatusUpdate(member.id, value)}>
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="expired">Expired</SelectItem>
                                  <SelectItem value="suspended">Suspended</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select onValueChange={(value) => handlePaymentStatusUpdate(member.id, value)}>
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue placeholder="Payment" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="paid">Paid</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="overdue">Overdue</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {!member.auth_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addAuthCredentials(member.email, member.full_name)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 ml-2"
                                title="Add login credentials for this member"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateMemberDates(
                                member.id, 
                                member.full_name, 
                                member.membership_start_date,
                                member.member_end_date
                              )}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 ml-2"
                              title="Update membership dates"
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMember(member.email, member.full_name)}
                              disabled={deletingMember === member.email}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                            >
                              {deletingMember === member.email ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <UserMinus className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Add New Member */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Add New Member
                </CardTitle>
                <CardDescription>
                  Manually add a new member to the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email Address</label>
                    <Input
                      type="email"
                      placeholder="member@example.com"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input
                      placeholder="John Doe"
                      value={newMemberName}
                      onChange={(e) => setNewMemberName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Password</label>
                    <Input
                      type="password"
                      placeholder="Temporary password"
                      value={newMemberPassword}
                      onChange={(e) => setNewMemberPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Membership Type</label>
                    <Select value={selectedMembershipType} onValueChange={setSelectedMembershipType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select membership type" />
                      </SelectTrigger>
                      <SelectContent>
                        {membershipTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name} - €{type.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button 
                    onClick={handleAddMember}
                    disabled={addingMember || !newMemberEmail || !newMemberName || !newMemberPassword || !selectedMembershipType}
                    className="flex items-center gap-2"
                  >
                    {addingMember ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding Member...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" />
                        Add Member
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={testEdgeFunction}
                    variant="outline"
                    className="flex items-center gap-2 ml-2"
                  >
                    Test Edge Function
                  </Button>
                  <Button 
                    onClick={handleAddMemberDirectly}
                    disabled={addingMember || !newMemberEmail || !newMemberName || !selectedMembershipType}
                    variant="secondary"
                    className="flex items-center gap-2 ml-2"
                  >
                    {addingMember ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" />
                        Add Direct (Profile Only)
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleAddMemberMinimal}
                    disabled={addingMember || !newMemberEmail || !newMemberName}
                    variant="ghost"
                    className="flex items-center gap-2 ml-2"
                  >
                    {addingMember ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        Testing...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" />
                        Test Minimal
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Resource Management</CardTitle>
                    <CardDescription>Manage downloadable resources and documents</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSaveChanges('resources')} 
                      disabled={saveLoading}
                      variant={hasUnsavedChanges.resources ? 'default' : 'outline'}
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      {saveLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Resource
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Resource</DialogTitle>
                        <DialogDescription>Create a new downloadable resource for members</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="title" className="text-right">Title</Label>
                          <Input
                            id="title"
                            value={newResource.title}
                            onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="description" className="text-right">Description</Label>
                          <Textarea
                            id="description"
                            value={newResource.description}
                            onChange={(e) => setNewResource({...newResource, description: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="file_url" className="text-right">File URL</Label>
                          <Input
                            id="file_url"
                            value={newResource.file_url}
                            onChange={(e) => setNewResource({...newResource, file_url: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="file_type" className="text-right">File Type</Label>
                          <Select onValueChange={(value) => setNewResource({...newResource, file_type: value})}>
                            <SelectTrigger className="col-span-3">
                              <SelectValue placeholder="Select file type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PDF">PDF</SelectItem>
                              <SelectItem value="DOCX">DOCX</SelectItem>
                              <SelectItem value="XLSX">XLSX</SelectItem>
                              <SelectItem value="PPTX">PPTX</SelectItem>
                              <SelectItem value="ZIP">ZIP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="category" className="text-right">Category</Label>
                          <Input
                            id="category"
                            value={newResource.category}
                            onChange={(e) => setNewResource({...newResource, category: e.target.value})}
                            className="col-span-3"
                            placeholder="e.g., Documentation, Tools, Templates"
                          />
                        </div>
                      </div>
                      <Button onClick={handleAddResource}>Add Resource</Button>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>File Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {resources.map((resource) => (
                        <TableRow key={resource.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{resource.title}</div>
                              <div className="text-sm text-muted-foreground">{resource.description}</div>
                            </div>
                          </TableCell>
                          <TableCell>{resource.category}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{resource.file_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={resource.is_active ? 'default' : 'secondary'}>
                              {resource.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditResource(resource)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteResource(resource.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Course Management</CardTitle>
                    <CardDescription>Manage training courses and educational content</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSaveChanges('courses')} 
                      disabled={saveLoading}
                      variant={hasUnsavedChanges.courses ? 'default' : 'outline'}
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      {saveLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Course
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Course</DialogTitle>
                        <DialogDescription>Create a new training course</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="course_title" className="text-right">Title</Label>
                          <Input
                            id="course_title"
                            value={newCourse.title}
                            onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="course_description" className="text-right">Description</Label>
                          <Textarea
                            id="course_description"
                            value={newCourse.description}
                            onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="instructor" className="text-right">Instructor</Label>
                          <Input
                            id="instructor"
                            value={newCourse.instructor}
                            onChange={(e) => setNewCourse({...newCourse, instructor: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="duration" className="text-right">Duration (hours)</Label>
                          <Input
                            id="duration"
                            type="number"
                            value={newCourse.duration_hours}
                            onChange={(e) => setNewCourse({...newCourse, duration_hours: parseInt(e.target.value)})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="price" className="text-right">Price (EUR)</Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            value={newCourse.price_eur}
                            onChange={(e) => setNewCourse({...newCourse, price_eur: parseFloat(e.target.value)})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="course_url" className="text-right">Course URL</Label>
                          <Input
                            id="course_url"
                            value={newCourse.course_url}
                            onChange={(e) => setNewCourse({...newCourse, course_url: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="course_category" className="text-right">Category</Label>
                          <Input
                            id="course_category"
                            value={newCourse.category}
                            onChange={(e) => setNewCourse({...newCourse, category: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="difficulty" className="text-right">Difficulty</Label>
                          <Select onValueChange={(value) => setNewCourse({...newCourse, difficulty_level: value})}>
                            <SelectTrigger className="col-span-3">
                              <SelectValue placeholder="Select difficulty" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Beginner">Beginner</SelectItem>
                              <SelectItem value="Intermediate">Intermediate</SelectItem>
                              <SelectItem value="Advanced">Advanced</SelectItem>
                              <SelectItem value="Expert">Expert</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button onClick={handleAddCourse}>Add Course</Button>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Course</TableHead>
                        <TableHead>Instructor</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Difficulty</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {courses.map((course) => (
                        <TableRow key={course.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{course.title}</div>
                              <div className="text-sm text-muted-foreground">{course.category}</div>
                            </div>
                          </TableCell>
                          <TableCell>{course.instructor}</TableCell>
                          <TableCell>{course.duration_hours}h</TableCell>
                          <TableCell>€{course.price_eur}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{course.difficulty_level}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditCourse(course)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteCourse(course.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Event Management</CardTitle>
                    <CardDescription>Manage webinars, workshops, and conferences</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSaveChanges('events')} 
                      disabled={saveLoading}
                      variant={hasUnsavedChanges.events ? 'default' : 'outline'}
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      {saveLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Event
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Event</DialogTitle>
                        <DialogDescription>Create a new event or webinar</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="event_title" className="text-right">Title</Label>
                          <Input
                            id="event_title"
                            value={newEvent.title}
                            onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="event_description" className="text-right">Description</Label>
                          <Textarea
                            id="event_description"
                            value={newEvent.description}
                            onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="event_date" className="text-right">Date & Time</Label>
                          <Input
                            id="event_date"
                            type="datetime-local"
                            value={newEvent.event_date}
                            onChange={(e) => setNewEvent({...newEvent, event_date: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="location" className="text-right">Location</Label>
                          <Input
                            id="location"
                            value={newEvent.location}
                            onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                            className="col-span-3"
                            placeholder="e.g., Virtual, London, UK"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="event_type" className="text-right">Event Type</Label>
                          <Select onValueChange={(value) => setNewEvent({...newEvent, event_type: value})}>
                            <SelectTrigger className="col-span-3">
                              <SelectValue placeholder="Select event type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Webinar">Webinar</SelectItem>
                              <SelectItem value="Workshop">Workshop</SelectItem>
                              <SelectItem value="Conference">Conference</SelectItem>
                              <SelectItem value="Seminar">Seminar</SelectItem>
                              <SelectItem value="Training">Training</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="registration_url" className="text-right">Registration URL</Label>
                          <Input
                            id="registration_url"
                            value={newEvent.registration_url}
                            onChange={(e) => setNewEvent({...newEvent, registration_url: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                      </div>
                      <Button onClick={handleAddEvent}>Add Event</Button>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{event.title}</div>
                              <div className="text-sm text-muted-foreground">{event.description?.substring(0, 50)}...</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {event.event_date ? new Date(event.event_date).toLocaleDateString() : 'TBD'}
                          </TableCell>
                          <TableCell>{event.location}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{event.event_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={event.is_active ? 'default' : 'secondary'}>
                              {event.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditEvent(event)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteEvent(event.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Achievement Management</CardTitle>
                    <CardDescription>Manage badges and achievements for members</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleSaveChanges('achievements')} 
                      disabled={saveLoading}
                      variant={hasUnsavedChanges.achievements ? 'default' : 'outline'}
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      {saveLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Achievement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New Achievement</DialogTitle>
                        <DialogDescription>Create a new badge or achievement</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="achievement_title" className="text-right">Title</Label>
                          <Input
                            id="achievement_title"
                            value={newAchievement.title}
                            onChange={(e) => setNewAchievement({...newAchievement, title: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="achievement_description" className="text-right">Description</Label>
                          <Textarea
                            id="achievement_description"
                            value={newAchievement.description}
                            onChange={(e) => setNewAchievement({...newAchievement, description: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="badge_image_url" className="text-right">Badge Image URL</Label>
                          <Input
                            id="badge_image_url"
                            value={newAchievement.badge_image_url}
                            onChange={(e) => setNewAchievement({...newAchievement, badge_image_url: e.target.value})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="criteria" className="text-right">Criteria</Label>
                          <Textarea
                            id="criteria"
                            value={newAchievement.criteria}
                            onChange={(e) => setNewAchievement({...newAchievement, criteria: e.target.value})}
                            className="col-span-3"
                            placeholder="How to earn this achievement"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="points" className="text-right">Points</Label>
                          <Input
                            id="points"
                            type="number"
                            value={newAchievement.points}
                            onChange={(e) => setNewAchievement({...newAchievement, points: parseInt(e.target.value)})}
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="achievement_category" className="text-right">Category</Label>
                          <Input
                            id="achievement_category"
                            value={newAchievement.category}
                            onChange={(e) => setNewAchievement({...newAchievement, category: e.target.value})}
                            className="col-span-3"
                            placeholder="e.g., Learning, Engagement, Community"
                          />
                        </div>
                      </div>
                      <Button onClick={handleAddAchievement}>Add Achievement</Button>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Achievement</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Points</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {achievements.map((achievement) => (
                        <TableRow key={achievement.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{achievement.title}</div>
                              <div className="text-sm text-muted-foreground">{achievement.description}</div>
                            </div>
                          </TableCell>
                          <TableCell>{achievement.category}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{achievement.points} pts</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={achievement.is_active ? 'default' : 'secondary'}>
                              {achievement.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditAchievement(achievement)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteAchievement(achievement.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Manual Achievement Management */}
            <Card>
              <CardHeader>
                <CardTitle>Manual Achievement Management</CardTitle>
                <CardDescription>Award or remove achievements for specific members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Award Achievement Section */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-4 text-green-700">Award Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="award-email">Member Email</Label>
                        <Input
                          id="award-email"
                          placeholder="member@example.com"
                          value={awardEmail}
                          onChange={(e) => setAwardEmail(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="award-achievement">Achievement</Label>
                        <Select value={selectedAchievementToAward} onValueChange={setSelectedAchievementToAward}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select achievement" />
                          </SelectTrigger>
                          <SelectContent>
                            {achievements.map((achievement) => (
                              <SelectItem key={achievement.id} value={achievement.title}>
                                {achievement.title} ({achievement.points} pts)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={handleAwardAchievement}
                          disabled={!awardEmail || !selectedAchievementToAward || awardingAchievement}
                          className="w-full"
                        >
                          {awardingAchievement ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Awarding...
                            </>
                          ) : (
                            <>
                              <Award className="h-4 w-4 mr-2" />
                              Award Achievement
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Remove Achievement Section */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-4 text-red-700">Remove Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="remove-email">Member Email</Label>
                        <Input
                          id="remove-email"
                          placeholder="member@example.com"
                          value={removeEmail}
                          onChange={(e) => setRemoveEmail(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="remove-achievement">Achievement</Label>
                        <Select value={selectedAchievementToRemove} onValueChange={setSelectedAchievementToRemove}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select achievement" />
                          </SelectTrigger>
                          <SelectContent>
                            {achievements.map((achievement) => (
                              <SelectItem key={achievement.id} value={achievement.title}>
                                {achievement.title} ({achievement.points} pts)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button 
                          variant="destructive"
                          onClick={handleRemoveAchievement}
                          disabled={!removeEmail || !selectedAchievementToRemove || removingAchievement}
                          className="w-full"
                        >
                          {removingAchievement ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Removing...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove Achievement
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Auto-Achievement Status */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-blue-700">Automatic Achievement System</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleRefreshAllAchievements}
                        disabled={refreshingAchievements}
                        className="bg-white hover:bg-blue-50"
                      >
                        {refreshingAchievements ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh All Achievements
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-blue-600 mb-3">
                      The following achievements are automatically awarded when members perform certain actions:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-blue-600" />
                        <span><strong>Welcome to CSI:</strong> New member registration</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-blue-600" />
                        <span><strong>First Enquiry:</strong> First general enquiry submission</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-blue-600" />
                        <span><strong>Course Explorer:</strong> First course enrollment request</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-blue-600" />
                        <span><strong>Active Member:</strong> Multiple platform interactions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enrollment Requests Tab */}
          <TabsContent value="enrollments" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>Course Enrollment Requests</CardTitle>
                  <CardDescription>Manage course enrollment requests from members</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    console.log('Refreshing enrollment requests...');
                    fetchEnrollmentRequests();
                    toast({
                      title: "Refreshed",
                      description: "Enrollment requests have been refreshed.",
                    });
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Course</TableHead>
                        <TableHead>Pricing</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollmentRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{request.user_name}</div>
                              <div className="text-sm text-muted-foreground">{request.user_email}</div>
                              <div className="text-xs text-muted-foreground">{request.membership_type}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{request.course_title}</div>
                              <div className="text-sm text-muted-foreground">{request.course_instructor}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="text-sm line-through text-muted-foreground">€{request.original_price?.toFixed(2)}</div>
                              <div className="font-medium text-green-600">€{request.discounted_price?.toFixed(2)}</div>
                              <div className="text-xs text-green-600">{request.discount_percentage}% OFF</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={request.status === 'pending' ? 'secondary' : request.status === 'processed' ? 'default' : 'destructive'}>
                              {request.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  // Mark as processed
                                  supabase
                                    .from('course_enrollment_requests_2025_12_03_12_00')
                                    .update({ status: 'processed' })
                                    .eq('id', request.id)
                                    .then(() => fetchEnrollmentRequests());
                                }}
                                disabled={request.status === 'processed'}
                              >
                                Mark Processed
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  // Create mailto link
                                  const subject = encodeURIComponent('Course Enrollment: ' + request.course_title);
                                  const body = encodeURIComponent(
                                    'Dear ' + request.user_name + ',\n\n' +
                                    'Thank you for your interest in ' + request.course_title + '.\n\n' +
                                    'Course Details:\n' +
                                    '- Title: ' + request.course_title + '\n' +
                                    '- Instructor: ' + request.course_instructor + '\n' +
                                    '- Your Price: €' + request.discounted_price?.toFixed(2) + ' (' + request.discount_percentage + '% member discount)\n\n' +
                                    'Please reply to this email to proceed with your enrollment.\n\n' +
                                    'Best regards,\nCSI Team'
                                  );
                                  window.open('mailto:' + request.user_email + '?subject=' + subject + '&body=' + body);
                                }}
                              >
                                Contact Member
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={async () => {
                                    if (confirm('Are you sure you want to delete this enrollment request from ' + request.user_name + '?')) {
                                    const { error } = await supabase
                                      .from('course_enrollment_requests_2025_12_03_12_00')
                                      .delete()
                                      .eq('id', request.id);
                                    if (!error) {
                                      fetchEnrollmentRequests();
                                      toast({
                                        title: "Request Deleted",
                                        description: "Enrollment request has been deleted.",
                                      });
                                    } else {
                                      toast({
                                        title: "Error",
                                        description: "Failed to delete request.",
                                        variant: "destructive",
                                      });
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {enrollmentRequests.length === 0 && (
                    <div className="text-center py-8">
                      <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No enrollment requests yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Enquiries Tab */}
          <TabsContent value="enquiries" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>General Enquiries</CardTitle>
                  <CardDescription>Manage member enquiries and support requests</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    console.log('Refreshing general enquiries...');
                    fetchGeneralEnquiries();
                    toast({
                      title: "Refreshed",
                      description: "General enquiries have been refreshed.",
                    });
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Enquiry</TableHead>
                        <TableHead>Type & Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {generalEnquiries.map((enquiry) => (
                        <TableRow key={enquiry.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{enquiry.user_name}</div>
                              <div className="text-sm text-muted-foreground">{enquiry.user_email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{enquiry.subject}</div>
                              <div className="text-sm text-muted-foreground max-w-xs truncate">
                                {enquiry.message}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="outline" className="text-xs capitalize">
                                {enquiry.enquiry_type}
                              </Badge>
                              <Badge 
                                variant="secondary"
                                  className={'text-xs block w-fit ' + (
                                    enquiry.priority === 'urgent' ? 'bg-red-100 text-red-800 border-red-300' :
                                    enquiry.priority === 'high' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                    enquiry.priority === 'normal' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                    'bg-gray-100 text-gray-800 border-gray-300'
                                  )}
                              >
                                {enquiry.priority}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                enquiry.status === 'pending' ? 'secondary' :
                                enquiry.status === 'in_progress' ? 'default' :
                                enquiry.status === 'resolved' ? 'default' : 'outline'
                              }
                                className={
                                  enquiry.status === 'resolved' ? 'bg-green-100 text-green-800 border-green-300' :
                                  enquiry.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''
                                }
                            >
                              {enquiry.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {new Date(enquiry.created_at).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {enquiry.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={async () => {
                                    const { error } = await supabase
                                      .from('general_enquiries_2025_12_03_13_00')
                                      .update({ status: 'in_progress' })
                                      .eq('id', enquiry.id);
                                    if (!error) {
                                      fetchGeneralEnquiries();
                                      toast({
                                        title: "Status Updated",
                                        description: "Enquiry marked as in progress.",
                                      });
                                    }
                                  }}
                                >
                                  Start
                                </Button>
                              )}
                              {(enquiry.status === 'pending' || enquiry.status === 'in_progress') && (
                                <Button 
                                  size="sm" 
                                  onClick={async () => {
                                    const { error } = await supabase
                                      .from('general_enquiries_2025_12_03_13_00')
                                      .update({ 
                                        status: 'resolved', 
                                        resolved_at: new Date().toISOString() 
                                      })
                                      .eq('id', enquiry.id);
                                    if (!error) {
                                      fetchGeneralEnquiries();
                                      toast({
                                        title: "Enquiry Resolved",
                                        description: "Enquiry has been marked as resolved.",
                                      });
                                    }
                                  }}
                                >
                                  Done
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                    const subject = encodeURIComponent('Re: ' + enquiry.subject);
                                    const body = encodeURIComponent(
                                      'Dear ' + enquiry.user_name + ',\n\n' +
                                      'Thank you for your enquiry about ' + enquiry.enquiry_type + '.\n\n' +
                                      'Your message: "' + enquiry.message + '"\n\n' +
                                      '[Please provide your response here]\n\n' +
                                      'Best regards,\nCSI Admin Team'
                                    );
                                    window.open('mailto:' + enquiry.user_email + '?subject=' + subject + '&body=' + body);
                                }}
                              >
                                Reply
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={async () => {
                                    if (confirm('Are you sure you want to delete this enquiry from ' + enquiry.user_name + '?')) {
                                    const { error } = await supabase
                                      .from('general_enquiries_2025_12_03_13_00')
                                      .delete()
                                      .eq('id', enquiry.id);
                                    if (!error) {
                                      fetchGeneralEnquiries();
                                      toast({
                                        title: "Enquiry Deleted",
                                        description: "Enquiry has been deleted.",
                                      });
                                    } else {
                                      toast({
                                        title: "Error",
                                        description: "Failed to delete enquiry.",
                                        variant: "destructive",
                                      });
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {generalEnquiries.length === 0 && (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No general enquiries yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Edit {editType === 'resource' ? 'Resource' : 
                      editType === 'course' ? 'Course' : 
                      editType === 'event' ? 'Event' : 'Achievement'}
              </DialogTitle>
              <DialogDescription>
                Update the {editType} information
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {editType === 'resource' && editingResource && (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_title" className="text-right">Title</Label>
                    <Input
                      id="edit_title"
                      value={editingResource.title}
                      onChange={(e) => setEditingResource({...editingResource, title: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_description" className="text-right">Description</Label>
                    <Textarea
                      id="edit_description"
                      value={editingResource.description}
                      onChange={(e) => setEditingResource({...editingResource, description: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_file_url" className="text-right">File URL</Label>
                    <Input
                      id="edit_file_url"
                      value={editingResource.file_url}
                      onChange={(e) => setEditingResource({...editingResource, file_url: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_category" className="text-right">Category</Label>
                    <Input
                      id="edit_category"
                      value={editingResource.category}
                      onChange={(e) => setEditingResource({...editingResource, category: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                </>
              )}
              
              {editType === 'course' && editingCourse && (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_course_title" className="text-right">Title</Label>
                    <Input
                      id="edit_course_title"
                      value={editingCourse.title}
                      onChange={(e) => setEditingCourse({...editingCourse, title: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_instructor" className="text-right">Instructor</Label>
                    <Input
                      id="edit_instructor"
                      value={editingCourse.instructor}
                      onChange={(e) => setEditingCourse({...editingCourse, instructor: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_price" className="text-right">Price (EUR)</Label>
                    <Input
                      id="edit_price"
                      type="number"
                      step="0.01"
                      value={editingCourse.price_eur}
                      onChange={(e) => setEditingCourse({...editingCourse, price_eur: parseFloat(e.target.value)})}
                      className="col-span-3"
                    />
                  </div>
                </>
              )}
              
              {editType === 'event' && editingEvent && (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_event_title" className="text-right">Title</Label>
                    <Input
                      id="edit_event_title"
                      value={editingEvent.title}
                      onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_location" className="text-right">Location</Label>
                    <Input
                      id="edit_location"
                      value={editingEvent.location}
                      onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_event_date" className="text-right">Date & Time</Label>
                    <Input
                      id="edit_event_date"
                      type="datetime-local"
                      value={editingEvent.event_date}
                      onChange={(e) => setEditingEvent({...editingEvent, event_date: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                </>
              )}
              
              {editType === 'achievement' && editingAchievement && (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_achievement_title" className="text-right">Title</Label>
                    <Input
                      id="edit_achievement_title"
                      value={editingAchievement.title}
                      onChange={(e) => setEditingAchievement({...editingAchievement, title: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_points" className="text-right">Points</Label>
                    <Input
                      id="edit_points"
                      type="number"
                      value={editingAchievement.points}
                      onChange={(e) => setEditingAchievement({...editingAchievement, points: parseInt(e.target.value)})}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="edit_achievement_category" className="text-right">Category</Label>
                    <Input
                      id="edit_achievement_category"
                      value={editingAchievement.category}
                      onChange={(e) => setEditingAchievement({...editingAchievement, category: e.target.value})}
                      className="col-span-3"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={
                editType === 'resource' ? handleUpdateResource :
                editType === 'course' ? handleUpdateCourse :
                editType === 'event' ? handleUpdateEvent :
                handleUpdateAchievement
              }>
                Update {editType === 'resource' ? 'Resource' : 
                        editType === 'course' ? 'Course' : 
                        editType === 'event' ? 'Event' : 'Achievement'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EnhancedAdminDashboard;